import ErrorBox from "lib/components/common/error-box";
import LoadingBox from "lib/components/common/loading-box";
import SimpleCard from "lib/components/common/simple-card";
import DashboardLayout from "lib/components/layouts/dashboard";
import useClinics from "lib/hooks/useClinics";
import useSubscriptions from "lib/hooks/useSubscription";
import { useContext, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import SubscriptionsCartPartialPage from "./subscriptions-cart";
import { PAGE_ROUTES } from "lib/constants/page-routes";
import usePractitioner from "lib/hooks/usePractitioner";
import { AppContext } from "lib/contexts/app-context";
import useRooms from "lib/hooks/useRooms";
import usePractitionerSubscriptions from "lib/hooks/usePractitionerSubscriptions";
import ClinicsDropdown from "lib/components/common/dashboard/clinics-dropdown";
import ClinicRoomsDropdown from "lib/components/common/dashboard/clinic-rooms-dropdown";
import SimpleDocumentSlider from "lib/components/common/simple-doc-slider";
import ROOM_BOOKING_SERVICE from "lib/services/room-booking-service";
import { mergeArrays } from "lib/utils/array-utils";
import { toast } from "sonner";

const breadcrumbItems: BreadcrumbItem[] = [
  {
    label: "Subscriptions",
    path: PAGE_ROUTES.PRACTITIONER.Subscription,
  },
  {
    label: "Buy Subscriptions",
    path: PAGE_ROUTES.PRACTITIONER.BuySubscription,
  },
];

const PractitionerSubscriptionPage = () => {
  const { appState } = useContext(AppContext);

  const {
    clinics,
    getClinics,
    getCalenderDetails,
    loading: clinicsLoading,
    error: clinicsError,
  } = useClinics();

  const {
    rooms,
    getRooms,
    getRoomImages,
    loading: roomsLoading,
    error: roomsError,
  } = useRooms();

  const {
    getPractitionerDetails,
    loading: practitionerLoading,
    error: practitionerError,
  } = usePractitioner();

  const {
    subscriptions,
    getSubscriptionsByClinic,
    loading: practitionerSubscriptionsLoading,
    error: practitionerSubscriptionsError,
  } = usePractitionerSubscriptions();

  const {
    buySubscription,
    error: subscriptionError,
    loading: subscriptionsLoading,
  } = useSubscriptions();

  //const [subscriptions, setSubscriptions] = useState<SubscriptionDetails[]>([]);
  //const [clinics, setClinics] = useState<Clinic[]>([]);
  //const [rooms, setRooms] = useState<RoomDetails[]>([]);

  const [selectedClinic, setSelectedClinic] = useState<Clinic | null>(null);
  const [selectedRoom, setSelectedRoom] = useState<RoomDetails | null>(null);
  const [calendarDetails, setCalendarDetails] = useState<CalendarDetails>(null);
  const [practitionerDetails, setPractitionerDetails] =
    useState<PractitionerDetails>(null);
  const [dateValidationError, setDateValidationError] = useState<string>("");

  useEffect(() => {
    getClinics(true, (clinics) => {
      if (clinics?.length > 0) {
        setSelectedClinic(clinics?.[0]);
        handleClinicChange(clinics?.[0]);
      }
    });

    // getSubscriptions((subscriptions) => {
    //   setSubscriptions(subscriptions);
    // });

    getPractitionerDetails(appState?.user_id, setPractitionerDetails);
  }, []);

  const handleSubscriptionSuccess = (url: string) => {
    window.open(url, "_self");
  };

  const handleClinicChange = (clinic: Clinic) => {
    setSelectedClinic(clinic);

    getSubscriptionsByClinic(clinic?.id);
    getRooms(clinic?.id, (rooms) => {
      if (rooms.length > 0) {
        handleRoomChange(rooms[0]);
      } else {
        setSelectedRoom(null);
      }
    });
    getCalenderDetails(clinic?.id, (calendar) => {
      setCalendarDetails(calendar);
    });
  };

  const handleRoomChange = (room: RoomDetails) => {
    getRoomImages(selectedClinic?.id, room?.id, (images) => {
      setSelectedRoom({ ...room, images });
    });
  };

  const handleCheckout = (details: SubscriptionCheckoutDetails) => {
    if (!details.fromDate || !details.toDate) {
      setDateValidationError("Please select both From Date and To Date.");
      toast.error("Please select both From Date and To Date.");
      return;
    }
    setDateValidationError("");
    const payload = {
      id: "",
      subscriptionId: details.subscriptionId,
      practitionerId: details.practitionerId,
      practitionerEmail: details?.practitionerEmail,
      clinicId: selectedClinic?.id,
      roomId: selectedRoom?.id,
      dates: details?.dates,
      totalCost: details?.dates?.length * selectedRoom?.pricePerDay,
      isActive: false,
      months: details?.months,
      perDayPrice: selectedRoom?.pricePerDay,
      room: selectedRoom?.roomNumber,
      weekDays: details?.weekDays,
      fromDate: details?.fromDate,
      toDate: details?.toDate,
    };
    console.log(payload);
    
    buySubscription(payload, handleSubscriptionSuccess);
  };

  /** Calculated Vars */
  const error =
    clinicsError ||
    subscriptionError ||
    practitionerError ||
    roomsError ||
    practitionerSubscriptionsError;
  const loading =
    clinicsLoading ||
    subscriptionsLoading ||
    practitionerLoading ||
    //roomsLoading ||
    practitionerSubscriptionsLoading;

  const clinicUnavailableDays = mergeArrays<UnavailableDay>(
    calendarDetails?.[0]?.unavailableDates ?? [],
    calendarDetails?.[0]?.holidays ?? []
  );

  const clinicUnavailableDates = (clinicUnavailableDays ?? []).map(
    (item) => new Date(item.date)
  );

  const unavailableDays = ROOM_BOOKING_SERVICE.getUnavailableRoomDates(
    subscriptions,
    selectedRoom?.id
  ).concat(clinicUnavailableDays);

  const unavailableDates = ROOM_BOOKING_SERVICE.getBookedRoomDates(
    subscriptions,
    selectedRoom?.id
  ).concat(clinicUnavailableDates);

  /** Calculated Vars */

  return (
    <DashboardLayout breadcrumbItems={breadcrumbItems}>
      <div>
        <h2>Book Rooms</h2>
        <p className=" text-start">Make a room booking for your clinic.</p>
      </div>
      {error ? (
        <ErrorBox message={error} />
      ) : loading ? (
        <LoadingBox message="Fetching clinics" />
      ) : (
        <SimpleCard>
          <Row>
            <Col sm="4">
              {/* <SimpleDropdown
                label="Select Clinic"
                options={clinics.map((clinic) => clinic.name)}
                values={clinics.map((clinic) => clinic.id)}
                selectedValue={selectedClinic?.id}
                onChange={(e) => {
                  const clinic = clinics.find((c) => c.id === e.target.value);
                  setSelectedClinic(clinic || null);
                  setCalendarDetails(null);
                  setSelectedSubscription(null);
                  //setRooms([]); // Reset rooms when clinic changes
                  setSelectedRoom(null); // Reset selected room
                }}
              /> */}

              <ClinicsDropdown
                clinics={clinics}
                onSelect={handleClinicChange}
                selectedClinic={selectedClinic}
                parentClassName="mb-3"
              />

              <ClinicRoomsDropdown
                clinicRooms={rooms}
                selectedRoom={selectedRoom}
                onRoomChange={handleRoomChange}
                parentClassName="mb-3"
              />

              {/* <SimpleDropdown
                label="Select Room"
                options={rooms.map((room) => `Room-${room.roomNumber}`)}
                values={rooms.map((room) => room.id)}
                selectedValue={selectedRoom?.id}
                onChange={(e) => {
                  const room = rooms.find((c) => c.id === e.target.value);
                  setSelectedRoom(room || null);
                  setCalendarDetails(null);
                  setSelectedSubscription(null);
                }}
              /> */}
              {/* Uncomment if you want to show documents slider */}
              {/* <SimpleDocumentSlider
                documents={[]}
                slideTo={0}
                autoHeight={true}
                isRenderAgain={true}
                onDelete={() => {}}
              /> */}
              <SimpleDocumentSlider
                documents={selectedRoom?.images ?? []}
                isLoading={roomsLoading}
                isError={roomsError}
                className="mb-3"
              />
              {/* <RoomBookingSummaryTile
                practitionerName={
                  practitionerDetails
                    ? getFullName(
                        practitionerDetails.firstName,
                        practitionerDetails.lastName
                      )
                    : ""
                }
                clinicName={selectedClinic ? selectedClinic.name : ""}
                roomNumber={selectedRoom ? selectedRoom.roomNumber : 0}
                dates={[]}
                pricePerDay={selectedRoom ? selectedRoom.pricePerDay : 0} // Assuming a fixed price per day for simplicity
              /> */}
            </Col>
            <Col sm="8">
              {selectedRoom && (
                <SubscriptionsCartPartialPage
                  //subscription={subscriptions[0]}
                  //practitionerName={appState?.name}
                  clinicName={selectedClinic?.name}
                  roomNumber={selectedRoom?.roomNumber}
                  perDayPrice={selectedRoom?.pricePerDay}
                  unavailableDays={unavailableDays}
                  unavailableDates={unavailableDates}
                  error={error}
                  loading={loading}
                  onCheckout={handleCheckout}
                  practitioner={practitionerDetails}
                  sundayDisabled={selectedClinic?.sundayClose || false}
                />
              )}
            </Col>
          </Row>
        </SimpleCard>
      )}

      {/* <div>
        <h2>Subscriptions</h2>
        <p className=" text-start">
          Choose a clinic and subscription plan that suits your needs.
        </p>
        {error ? (
          <ErrorBox message={error} />
        ) : loading ? (
          <LoadingBox message="Fetching clinics" />
        ) : (
          <Row className="overflow-auto mt-4 custom-scroll">
            {(clinics ?? []).map((clinic) => (
              <Col xs={12} sm={4} key={clinic.id}>
                <ClinicWidget
                  clinic={clinic}
                  onSelected={(value) => {
                    setSelectedClinic(value?.clinic);
                    setCalendarDetails(value?.calendar);
                    setSelectedSubscription(null);
                  }}
                  isSelected={selectedClinic?.id === clinic.id}
                />
              </Col>
            ))}
          </Row>
        )}
        {selectedClinic && !selectedSubscription && (
          <SimpleCard title={`Subscriptions for ${selectedClinic?.name}`}>
            {filteredSubscriptions.length === 0 ? (
              <>No subscriptions available</>
            ) : (
              <Row>
                {filteredSubscriptions.map((subscription) => (
                  <Col xs="12" sm="4" key={subscription.id}>
                    <SubscriptionCard
                      isAddOn={subscription.isAddOn}
                      subscription={{
                        ...subscription,
                        perDayPrice: subscription.perDayPrice,
                      }}
                      key={subscription.id}
                      onSubscribe={() => setSelectedSubscription(subscription)}
                    />
                  </Col>
                ))}
              </Row>
            )}
          </SimpleCard>
        )}
        {selectedSubscription && (
          <SimpleCard title={`Subscriptions for ${selectedClinic?.name}`}>
            <Row>
              <Col sm="4">
                <SubscriptionCard
                  isAddOn={selectedSubscription?.isAddOn}
                  subscription={{
                    ...selectedSubscription,
                    perDayPrice: selectedSubscription?.perDayPrice,
                  }}
                  key={selectedSubscription?.id}
                />
              </Col>
              <Col sm="8">
                <SubscriptionsCartPartialPage
                  subscription={selectedSubscription}
                  calendarDetails={calendarDetails}
                  error={error}
                  loading={loading}
                  onCheckout={(details) =>
                    buySubscription(details, handleSubscriptionSuccess)
                  }
                  practitionerDetails={practitionerDetails}
                />
              </Col>
            </Row>
          </SimpleCard>
        )}
      </div> */}
    </DashboardLayout>
  );
};

export default PractitionerSubscriptionPage;
